/**
 * In-app notifications hook — Notification Bell feature.
 * Manages notification list, unread count, and auto-polling.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

export interface InAppNotification {
  id: number;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
  read_at: string | null;
}

interface NotificationListResponse {
  data: InAppNotification[];
  unread_count: number;
}

interface UnreadCountResponse {
  count: number;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds

export function useNotifications() {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failCountRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiFetch<UnreadCountResponse>('/api/notifications/unread-count');
      setUnreadCount(res.count);
      failCountRef.current = 0;
    } catch {
      failCountRef.current++;
      // Stop polling after 3 consecutive failures to avoid flooding the console
      if (failCountRef.current >= 3 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, []);

  const fetchNotifications = useCallback(async (status: 'unread' | 'read' | 'all' = 'all') => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<NotificationListResponse>(`/api/notifications?status=${status}&limit=50`);
      setNotifications(res.data);
      setUnreadCount(res.unread_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Best-effort
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiFetch('/api/notifications/read-all', { method: 'PUT' });
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true, read_at: n.read_at ?? new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch {
      // Best-effort
    }
  }, []);

  const dismissNotification = useCallback(async (id: number) => {
    try {
      await apiFetch(`/api/notifications/${id}`, { method: 'DELETE' });
      setNotifications((prev) => {
        const removed = prev.find((n) => n.id === id);
        if (removed && !removed.read) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n.id !== id);
      });
    } catch {
      // Best-effort
    }
  }, []);

  // Auto-poll unread count
  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
  };
}
